import { Icon, FormField, InputText } from 'ui';
import FooterButtons from 'core/modules/compositions/FooterButtons';
import { ValidateEmail } from 'core/utils/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

// import Icon from '../../atoms/Icon';
// import { lessonType } from '../../../variables/constant';

const ForgotPasswordForm = ({ t, onCancel, onSuccess, state }) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const emailInputRef = useRef(null);
  const successRef = useRef(null);

  // WCAG 4.1.3 — Success arrives asynchronously and swaps the whole form out, unmounting the
  // input that held the focus. A live region is not enough here: it would be inserted together
  // with its text, which is the same reason the app toasts are never announced (TAN-10137).
  // Moving the focus is what reliably announces the message, and it also stops the focus from
  // falling back to the dialog container.
  useEffect(() => {
    if (state === 'success') successRef.current?.focus();
  }, [state]);

  function handleEmailChange(e) {
    const newEmail = e.currentTarget.value;
    setEmail(newEmail);
    // Reward early: clear a visible error as soon as the value becomes valid,
    // but never raise a new one while the user is still typing.
    if (emailError && ValidateEmail(newEmail.trim())) setEmailError('');
  }

  function handleEmailBlur() {
    // Blur both raises AND clears: leaving an empty field is not an error yet,
    // the submit guard covers that case.
    const hasInvalidEmail = email.trim().length > 0 && !ValidateEmail(email.trim());
    setEmailError(hasInvalidEmail ? t('auth:Enter a valid email address') : '');
  }

  function onSendEmail(e) {
    e?.preventDefault();
    if (!ValidateEmail(email.trim())) {
      setEmailError(t(email.trim() ? 'auth:Enter a valid email address' : 'auth:Enter your email'));
      emailInputRef.current?.focus();
      return;
    }
    onSuccess(email.trim());
  }

  return (
    <div className="forgot-password-form">
      {state === 'success' ? (
        <div className="forgot-password__success">
          <Icon name="check-checked-round-fill" color="primary" size="3xl" />
          {/* `tabIndex={-1}` makes this focusable by script only, never by Tab. No `role="status"`
              on purpose: with the focus landing here it would announce the message twice. */}
          <div className="forgot-password__success-text" ref={successRef} tabIndex={-1}>
            {t('auth:Great! Review you inbox to see the instructions')}
          </div>
        </div>
      ) : (
        <div>
          {/* `noValidate`: the native `required` injected by FormField would abort the submit
              event and replace our own error message with the browser bubble. */}
          <form noValidate onSubmit={onSendEmail}>
            <FormField error={!!emailError} errorMessage={emailError} isRequired label={t('auth:Type your email to receive a recover link')}>
              <InputText onBlur={handleEmailBlur} onChange={handleEmailChange} placeholder={t('tucorreo@email.com')} referer={emailInputRef} value={email} />
            </FormField>
          </form>
          {/* Kept outside the <form>: the cancel button is `type="reset"` and a native reset
              would desync the controlled input. */}
          <div className="new-lesson-form__buttons">
            <FooterButtons
              buttonTextCancel={t('actions:Cancel')}
              buttonTextSucess={t('actions:Send')}
              loading={state === 'loading'}
              onCancel={onCancel}
              onSuccess={onSendEmail}
            />
          </div>
        </div>
      )}
    </div>
  );
};

ForgotPasswordForm.propTypes = {
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  state: PropTypes.oneOf(['initial', 'loading', 'success']),
  t: PropTypes.func.isRequired,
};

ForgotPasswordForm.defaultProps = {
  onCancel: () => {},
  onSuccess: () => {},
};

export default ForgotPasswordForm;
